import { useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface Filters {
  search: string;
  category: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { search: '', category: '', dateFrom: '', dateTo: '' };

interface TrainingRow {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  course_name: string;
  institution: string;
  category: string;
  date: string;
  notes: string | null;
  has_certificate: boolean;
  certificate_filename: string | null;
}

interface UserOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const emptyForm = { user_id: '' as string | number, course_name: '', institution: '', category: '', date: '', notes: '' };

export function Training() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [addCertificate, setAddCertificate] = useState<File | null>(null);
  const [editing, setEditing] = useState<TrainingRow | null>(null);
  const [editForm, setEditForm] = useState({ course_name: '', institution: '', category: '', date: '', notes: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const canCreate = can('hr.create');
  const canUpdate = can('hr.update');
  const canArchive = can('hr.archive');

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      category: filters.category || undefined,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
    }),
    [filters]
  );

  const { data, loading, error, setPage, reload } = usePaginatedList<TrainingRow>('/training-records', query);

  const openAdd = () => {
    setForm(emptyForm);
    setAddCertificate(null);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('user_id', String(form.user_id));
      data.append('course_name', form.course_name);
      data.append('institution', form.institution);
      data.append('category', form.category);
      data.append('date', form.date);
      if (form.notes) data.append('notes', form.notes);
      if (addCertificate) data.append('certificate', addCertificate);
      await api.post('/training-records', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEdit = (row: TrainingRow) => {
    setEditing(row);
    setEditForm({
      course_name: row.course_name,
      institution: row.institution,
      category: row.category,
      date: row.date,
      notes: row.notes ?? '',
    });
    setErrors({});
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/training-records/${editing.id}`, editForm);
      setEditing(null);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (row: TrainingRow) => {
    if (!(await confirmAction(`Delete this training record for ${row.user_name}?`))) return;
    try {
      await api.delete(`/training-records/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Training</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All categories</option>
          {listOptions(lists, 'training_category').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <label className="checkbox-inline">
          Date from
          <input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Course</th>
                <th>Institution</th>
                <th>Category</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.id}>
                    <td className="muted">{row.employee_code ?? '—'}</td>
                    <td>
                      <strong>{row.user_name}</strong>
                    </td>
                    <td>{row.course_name}</td>
                    <td className="muted">{row.institution}</td>
                    <td>{labelFor(lists, 'training_category', row.category)}</td>
                    <td>{formatDate(row.date)}</td>
                    <td className="right">
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(row)} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No training records yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Add training record" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <SearchableSelect<UserOption>
                endpoint="/users"
                value={form.user_id}
                onChange={(id) => setForm({ ...form, user_id: id })}
                getLabel={(u) => u.full_name}
                placeholder="Search staff…"
              />
              {errors.user_id && <small className="error">{errors.user_id[0]}</small>}
            </label>
            <label className="span-2">
              Course *
              <input
                type="text"
                value={form.course_name}
                onChange={(e) => setForm({ ...form, course_name: e.target.value })}
                required
              />
              {errors.course_name && <small className="error">{errors.course_name[0]}</small>}
            </label>
            <label>
              Institution *
              <input
                type="text"
                value={form.institution}
                onChange={(e) => setForm({ ...form, institution: e.target.value })}
                required
              />
              {errors.institution && <small className="error">{errors.institution[0]}</small>}
            </label>
            <label>
              Category *
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required>
                <option value="">Select…</option>
                {listOptions(lists, 'training_category').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.category && <small className="error">{errors.category[0]}</small>}
            </label>
            <label>
              Date *
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              {errors.date && <small className="error">{errors.date[0]}</small>}
            </label>
            <label className="span-2">
              Notes
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
            <label className="span-2">
              Certificate
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setAddCertificate(e.target.files?.[0] ?? null)}
              />
              {errors.certificate && <small className="error">{errors.certificate[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!form.user_id || !form.course_name || !form.institution || !form.category || !form.date}
              >
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit record for ${editing.user_name}`} onClose={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Course *
              <input
                type="text"
                value={editForm.course_name}
                onChange={(e) => setEditForm({ ...editForm, course_name: e.target.value })}
                required
              />
              {errors.course_name && <small className="error">{errors.course_name[0]}</small>}
            </label>
            <label>
              Institution *
              <input
                type="text"
                value={editForm.institution}
                onChange={(e) => setEditForm({ ...editForm, institution: e.target.value })}
                required
              />
              {errors.institution && <small className="error">{errors.institution[0]}</small>}
            </label>
            <label>
              Category *
              <select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {listOptions(lists, 'training_category').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.category && <small className="error">{errors.category[0]}</small>}
            </label>
            <label>
              Date *
              <input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                required
              />
              {errors.date && <small className="error">{errors.date[0]}</small>}
            </label>
            <label className="span-2">
              Notes
              <textarea
                rows={3}
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
